import { useNavigate } from '@tanstack/react-router'
import {
  Route as RouteIcon,
  IdCardLanyard,
  CalendarDays,
  CircleUserRound,
  FileSearch2,
  ShieldCheck,
  Contact,
} from '@/components/icons'
import { useEffect, useRef, useState } from 'react'
import { Card } from '@/components/ui/card'
import { SearchInput } from '@/components/ui/search-input'
import { useAdminSearch } from '@/lib/hooks'

interface OperationsSearchDialogOverlayProps {
  isOpen: boolean
  onClose: () => void
  initialSearch?: string
}

export function OperationsSearchDialogOverlay({
  isOpen,
  onClose,
  initialSearch = '',
}: OperationsSearchDialogOverlayProps) {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState(initialSearch)
  const [debouncedQuery, setDebouncedQuery] = useState(initialSearch)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { data: searchResults, isFetching } = useAdminSearch(debouncedQuery)

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
    if (!isOpen) {
      setSearchQuery('')
      setDebouncedQuery('')
    }
  }, [isOpen])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
      navigate({
        to: '/operations',
        search: { search: searchQuery || undefined } as any,
        replace: true,
      })
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery, navigate])

  if (!isOpen) return null

  const hasResults =
    searchResults &&
    (searchResults.trips.length > 0 ||
      searchResults.crew.length > 0 ||
      searchResults.roster.length > 0 ||
      searchResults.passengers.length > 0 ||
      searchResults.crewDocuments.length > 0 ||
      searchResults.complianceDocuments.length > 0 ||
      searchResults.passengerDocuments.length > 0)

  const noResults =
    debouncedQuery &&
    searchResults &&
    !hasResults

  return (
    <div className="fixed inset-0 z-[60]" onClick={onClose}>
      <div className="absolute inset-0 bg-overlay-default" />
      <div
        className="relative px-4"
        style={{ paddingTop: 'max(1rem, var(--safe-top))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Card className="flex flex-col gap-4">
          <p className="text-sm font-medium text-content-primary-bold tracking-[0.07px] leading-normal">
            Search anything
          </p>
          <SearchInput
            ref={searchInputRef}
            grey
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            loading={isFetching}
            onBack={searchResults ? onClose : undefined}
            onClear={() => setSearchQuery('')}
          />
          {debouncedQuery && searchResults && (
            <div className="flex flex-col gap-3 overflow-y-auto max-h-[60vh]">
              {searchResults.trips.map((item) => (
                <SearchResultItem
                  key={item.id}
                  icon={<RouteIcon className="size-8 text-content-brand-bold" />}
                  title={item.label}
                  subtitle="Trips"
                  onClick={() => {
                    onClose()
                    navigate({
                      to: '/operations/trips/$tripId',
                      params: { tripId: item.id },
                    } as any)
                  }}
                />
              ))}
              {searchResults.crew.map((item) => (
                <SearchResultItem
                  key={item.id}
                  icon={<IdCardLanyard className="size-8 text-content-brand-bold" />}
                  title={item.label}
                  subtitle="Crew Directory"
                  onClick={() => {
                    onClose()
                    navigate({
                      to: '/operations/crew-directory/$crewId',
                      params: { crewId: item.id },
                    } as any)
                  }}
                />
              ))}
              {searchResults.roster.map((item) => (
                <SearchResultItem
                  key={item.id}
                  icon={
                    <CalendarDays className="size-8 text-content-brand-bold" />
                  }
                  title={item.label}
                  subtitle="Schedule"
                  onClick={() => {
                    onClose()
                    navigate({
                      to: '/operations/rosters',
                      search: {
                        tab: 'calendar',
                        filterCrewIds: [item.crewId],
                      } as any,
                    })
                  }}
                />
              ))}
              {searchResults.passengers.map((item) => (
                <SearchResultItem
                  key={item.id}
                  icon={
                    <CircleUserRound className="size-8 text-content-brand-bold" />
                  }
                  title={item.label}
                  subtitle="PAX Directory"
                  onClick={() => {
                    onClose()
                    navigate({
                      to: '/operations/pax/$passengerId',
                      params: { passengerId: item.id },
                    } as any)
                  }}
                />
              ))}
              {searchResults.crewDocuments.map((item) => (
                <SearchResultItem
                  key={`crew-doc-${item.id}`}
                  icon={
                    <FileSearch2 className="size-8 text-content-brand-bold" />
                  }
                  title={item.label}
                  subtitle="Crew Documents"
                  onClick={() => {
                    onClose()
                    navigate({
                      to: '/operations/crew-directory/$crewId',
                      params: { crewId: item.crewId },
                      search: { tab: 'documents', documentId: item.id },
                    } as any)
                  }}
                />
              ))}
              {searchResults.complianceDocuments.map((item) => (
                <SearchResultItem
                  key={`compliance-doc-${item.id}`}
                  icon={
                    <FileSearch2 className="size-8 text-content-brand-bold" />
                  }
                  title={item.label}
                  subtitle="Documents"
                  onClick={() => {
                    onClose()
                    navigate({
                      to: '/operations/documents',
                      search: { documentId: item.id },
                    } as any)
                  }}
                />
              ))}
              {searchResults.passengerDocuments.map((item) => (
                <SearchResultItem
                  key={`passenger-doc-${item.id}`}
                  icon={
                    <FileSearch2 className="size-8 text-content-brand-bold" />
                  }
                  title={item.label}
                  subtitle="PAX Documents"
                  onClick={() => {
                    onClose()
                    navigate({
                      to: '/operations/pax/$passengerId',
                      params: { passengerId: item.passengerId },
                      search: { tab: 'documents' },
                    } as any)
                  }}
                />
              ))}
              {noResults && (
                <p className="text-xs text-content-primary-subtle py-2 text-center">
                  No results found
                </p>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

function SearchResultItem({
  icon,
  title,
  subtitle,
  onClick,
}: {
  icon: React.ReactNode
  title: string
  subtitle: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 rounded-lg hover:bg-surface-L2 text-left"
    >
      <div className="shrink-0 p-2 rounded-lg bg-background-brand-subtle">
        {icon}
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-sm font-medium text-content-primary-bold tracking-[0.07px] truncate">
          {title}
        </span>
        <span className="text-xs text-content-primary-subtle tracking-[0.18px] truncate">
          {subtitle}
        </span>
      </div>
    </button>
  )
}
